import { useAuth0 } from "@auth0/auth0-react";
import React, { useState } from "react";
import { useComputeAllNextAutoTransactionsMutation, useRefetchStockDatasMutation, useResetAllPowensDataMutation, useSyncLastTransactionsForAllMutation, useTestSignalsMutation } from "../generated/graphql";
import ErrorMessage from "./ErrorMessage";
import SyncAccountCard from "./SyncAccountCard";
import Toast from "./Toast";
import { useToast } from "../hooks/useToast";

const Admin: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth0();
  const [computeAllNextAutoTransactions, { loading: loadingAuto, error: errorAuto }] = useComputeAllNextAutoTransactionsMutation();
  const [syncLastTransactionsForAll, { loading: loadingSync, error: errorSync }] = useSyncLastTransactionsForAllMutation();
  const [refetchStockDatas, { loading: loadingStocks, error: errorStocks }] = useRefetchStockDatasMutation();
  const [testSignals, { loading: loadingSignals, error: errorSignals }] = useTestSignalsMutation();
  const [resetAllPowensData, { loading: loadingReset, error: errorReset }] = useResetAllPowensDataMutation();
  const [resultAuto, setResultAuto] = useState<{ totalCount: number } | null>(null);
  const [resultSync, setResultSync] = useState<{ totalTransactionsCount: number; totalAccountsCount: number; usersCount: number } | null>(null);
  const [resultStocks, setResultStocks] = useState<{ count: number } | null>(null);
  const [resultSignals, setResultSignals] = useState<{ usersCount: number } | null>(null);
  const [resetDone, setResetDone] = useState(false);
  const { toast, setToast } = useToast();

  const handleCompute = async () => {
    try {
      setResultAuto(null);
      const response = await computeAllNextAutoTransactions();
      if (response.data?.computeAllNextAutoTransactions) {
        setResultAuto(response.data.computeAllNextAutoTransactions);
        setToast({
          message: `${response.data.computeAllNextAutoTransactions.totalCount} transaction(s) automatique(s) créée(s)`,
          type: "success",
        });
      }
    } catch (err: any) {
      console.error("Erreur lors du calcul des transactions automatiques:", err);
      // Les erreurs GraphQL sont déjà gérées par le hook via la prop `error`
      // On affiche un toast uniquement pour les erreurs non-GraphQL
      if (!errorAuto) {
        setToast({
          message: err?.message || "Une erreur est survenue lors du calcul des transactions automatiques",
          type: "error",
        });
      }
    }
  };

  const handleSync = async () => {
    try {
      setResultSync(null);
      const response = await syncLastTransactionsForAll();
      if (response.data?.syncLastTransactionsForAll) {
        const totalTransactionsCount = response.data.syncLastTransactionsForAll.reduce(
          (sum, user) => sum + (user.transactionsCount || 0),
          0
        );
        const totalAccountsCount = response.data.syncLastTransactionsForAll.reduce(
          (sum, user) => sum + (user.accountsCount || 0),
          0
        );
        const usersCount = response.data.syncLastTransactionsForAll.length;
        setResultSync({ totalTransactionsCount, totalAccountsCount, usersCount });
        
        const messages = [];
        if (totalTransactionsCount > 0) {
          messages.push(`${totalTransactionsCount} transaction(s) créée(s)`);
        }
        if (totalAccountsCount > 0) {
          messages.push(`${totalAccountsCount} compte(s) de groupe synchronisé(s)`);
        }
        if (messages.length > 0) {
          setToast({
            message: `${messages.join(' et ')} pour ${usersCount} utilisateur(s)`,
            type: "success",
          });
        } else {
          setToast({
            message: `Synchronisation terminée pour ${usersCount} utilisateur(s) (aucune nouvelle donnée)`,
            type: "success",
          });
        }
      }
    } catch (err: any) {
      console.error("Erreur lors de la synchronisation des transactions BudgetInside:", err);
      // Les erreurs GraphQL sont déjà gérées par le hook via la prop `error`
      // On affiche un toast uniquement pour les erreurs non-GraphQL
      if (!errorSync) {
        setToast({
          message: err?.message || "Une erreur est survenue lors de la synchronisation des transactions BudgetInside",
          type: "error",
        });
      }
    }
  };

  const handleRefetchStocks = async () => {
    try {
      setResultStocks(null);
      const response = await refetchStockDatas();
      if (response.data?.refetchStockDatas) {
        const count = response.data.refetchStockDatas.length;
        setResultStocks({ count });
        setToast({ message: `${count} action(s) synchronisée(s)`, type: "success" });
      }
    } catch (err: any) {
      if (!errorStocks) {
        setToast({ message: err?.message || "Une erreur est survenue lors de la synchro des stocks", type: "error" });
      }
    }
  };

  const handleTestSignals = async () => {
    try {
      setResultSignals(null);
      const response = await testSignals();
      if (response.data?.testSignals) {
        const usersCount = response.data.testSignals.length;
        setResultSignals({ usersCount });
        setToast({ message: `Signaux recalculés pour ${usersCount} utilisateur(s)`, type: "success" });
      }
    } catch (err: any) {
      if (!errorSignals) {
        setToast({ message: err?.message || "Une erreur est survenue lors du calcul des signaux", type: "error" });
      }
    }
  };

  const handleResetPowensData = async () => {
    if (!window.confirm("ATTENTION : cette opération efface toutes les données Powens (tokens, liens comptes) pour tous les utilisateurs. Les utilisateurs devront reconnecter leurs banques. Confirmer ?")) {
      return;
    }
    try {
      setResetDone(false);
      await resetAllPowensData();
      setResetDone(true);
      setToast({ message: "Données Powens réinitialisées avec succès", type: "success" });
    } catch (err: any) {
      if (!errorReset) {
        setToast({ message: err?.message || "Une erreur est survenue lors de la réinitialisation", type: "error" });
      }
    }
  };

  if (authLoading) {
    return (
      <div className="container mx-auto">
        <h1 className="text-2xl font-bold mb-6">Administration</h1>
        <div className="text-center text-gray-500">Chargement...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="container mx-auto">
        <h1 className="text-2xl font-bold mb-6">Administration</h1>
        <div className="text-center text-red-500">
          Veuillez vous connecter pour accéder à l'administration
        </div>
      </div>
    );
  }

  return (
    <>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <div className="container mx-auto">
        <h1 className="text-2xl font-bold mb-6">Administration</h1>
        
        {errorAuto && (
          <ErrorMessage
            error={errorAuto}
            onRetry={handleCompute}
            title="Erreur lors du calcul des transactions automatiques"
            className="mb-6"
          />
        )}

        {errorSync && (
          <ErrorMessage
            error={errorSync}
            onRetry={handleSync}
            title="Erreur lors de la synchronisation des transactions BudgetInside"
            className="mb-6"
          />
        )}

        {errorStocks && (
          <ErrorMessage
            error={errorStocks}
            onRetry={handleRefetchStocks}
            title="Erreur lors de la synchronisation des valeurs de stocks"
            className="mb-6"
          />
        )}

        {errorSignals && (
          <ErrorMessage
            error={errorSignals}
            onRetry={handleTestSignals}
            title="Erreur lors du calcul des signaux"
            className="mb-6"
          />
        )}

        {errorReset && (
          <ErrorMessage
            error={errorReset}
            onRetry={handleResetPowensData}
            title="Erreur lors de la réinitialisation des données Powens"
            className="mb-6"
          />
        )}

        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Transactions automatiques</h2>
          <p className="text-gray-600 mb-4">
            Calcule et crée les prochaines transactions automatiques pour tous les groupes.
          </p>
          
          <button
            onClick={handleCompute}
            disabled={loadingAuto}
            className="px-6 py-3 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {loadingAuto ? "Calcul en cours..." : "Calculer les transactions automatiques"}
          </button>

          {resultAuto && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800 font-medium">
                {resultAuto.totalCount} transaction(s) automatique(s) créée(s) avec succès
              </p>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Valeurs de stocks</h2>
          <p className="text-gray-600 mb-4">
            Récupère les dernières valeurs et indicateurs techniques (RSI, SMA, MACD…) pour toutes les actions depuis AlphaVantage. Opération longue (10–60 secondes).
          </p>

          <button
            onClick={handleRefetchStocks}
            disabled={loadingStocks}
            className="px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {loadingStocks ? "Synchronisation en cours..." : "Synchroniser les valeurs de stocks"}
          </button>

          {resultStocks && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800 font-medium">
                {resultStocks.count} action(s) synchronisée(s) avec succès
              </p>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Calcul des signaux</h2>
          <p className="text-gray-600 mb-4">
            Recalcule les signaux RSI et SMA pour tous les utilisateurs et envoie les notifications Slack si des signaux d'achat ou de vente sont détectés.
          </p>

          <button
            onClick={handleTestSignals}
            disabled={loadingSignals}
            className="px-6 py-3 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {loadingSignals ? "Calcul en cours..." : "Calculer les signaux"}
          </button>

          {resultSignals && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800 font-medium">
                Signaux recalculés pour {resultSignals.usersCount} utilisateur(s)
              </p>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg border border-red-200 p-6 mb-6">
          <h2 className="text-xl font-semibold mb-2 text-red-700">Réinitialisation Powens</h2>
          <p className="text-gray-600 mb-1">
            Efface toutes les données Powens pour tous les utilisateurs : tokens bancaires et liens entre comptes et banques.
          </p>
          <p className="text-red-600 text-sm mb-4 font-medium">
            ⚠ Opération irréversible — les utilisateurs devront reconnecter leurs banques.
          </p>

          <button
            onClick={handleResetPowensData}
            disabled={loadingReset}
            className="px-6 py-3 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {loadingReset ? "Réinitialisation en cours..." : "Réinitialiser les données Powens"}
          </button>

          {resetDone && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800 font-medium">
                Données Powens réinitialisées. Les utilisateurs peuvent reconnecter leurs banques via le nouveau flux.
              </p>
            </div>
          )}
        </div>

        <SyncAccountCard />

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-xl font-semibold mb-4">Synchronisation BudgetInside</h2>
          <p className="text-gray-600 mb-4">
            Synchronise les dernières transactions BudgetInside et les comptes de groupes pour tous les utilisateurs ayant des paramètres BudgetInside configurés.
          </p>
          
          <button
            onClick={handleSync}
            disabled={loadingSync}
            className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {loadingSync ? "Synchronisation en cours..." : "Synchroniser les transactions BudgetInside"}
          </button>

          {resultSync && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg space-y-2">
              <p className="text-green-800 font-medium">
                Synchronisation terminée pour {resultSync.usersCount} utilisateur(s)
              </p>
              <div className="text-green-700 text-sm space-y-1">
                {resultSync.totalTransactionsCount > 0 && (
                  <p>• {resultSync.totalTransactionsCount} transaction(s) BudgetInside créée(s)</p>
                )}
                {resultSync.totalAccountsCount > 0 && (
                  <p>• {resultSync.totalAccountsCount} compte(s) de groupe synchronisé(s)</p>
                )}
                {resultSync.totalTransactionsCount === 0 && resultSync.totalAccountsCount === 0 && (
                  <div className="p-4 bg-gray-50 rounded-sm border border-gray-200"><p className="text-gray-500 italic">Aucune nouvelle donnée synchronisée.</p></div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Admin;

